import { useState, type ReactElement, type SyntheticEvent } from 'react';

import { type ApiRefusal } from './api';
import { type SignedOutReason } from './account-state';
import { checkRegistration, checkSignIn, describeRefusal, messageFor, type FieldMessage } from './forms';

/**
 * The screen a visitor who is not signed in actually sees.
 *
 * It exists because the alternative was *"Could not reach the game server:
 * unauthorised"* over a blank canvas, which reads as a broken deployment rather
 * than as a person who has not signed in — and gives them nothing to do about
 * it either way.
 *
 * ## It says which of three things happened
 *
 * A first visit, a deliberate sign-out and a session that ended overnight all
 * render this same form and mean different things (`./account-state.ts`). The
 * third is the one that matters: without a sentence saying so, a returning
 * player has been silently logged out of a session they thought they had.
 *
 * ## No password reset, and no pretending there is one
 *
 * There is no reset and there cannot be one until this stack has a mail path
 * (`TODO.md` #390). A "forgot your password?" link that led nowhere, or to a
 * form that could not work, would be worse than its absence — so what is offered
 * instead is the truth: a password cannot be recovered, and a new account can be
 * made.
 *
 * ## Nothing here is stored, remembered or logged
 *
 * The password lives in a `useState` for as long as the form is on screen and
 * goes nowhere else. There is no "remember me" — the token's own lifetime is
 * the session (`./session-store.ts`) — and no `console` call in this file.
 */

/** What a visitor is told about why they are looking at this. */
const NOTICES: Readonly<Record<SignedOutReason, string | null>> = {
  // A stranger needs no explanation for a sign-in screen; the heading is the
  // explanation.
  'never-signed-in': null,
  'signed-out': 'You are signed out.',
  'session-ended': 'Your session ended, so you were signed out. Sign in again to pick up where you left off.',
};

export interface SignInScreenProps {
  readonly reason: SignedOutReason;
  readonly onSignIn: (credentials: { email: string; password: string }) => void;
  readonly onRegister: (credentials: { email: string; password: string; displayName: string }) => void;
  /** In flight. Both buttons are disabled, so one Enter key cannot spend two rate-limit allowances. */
  readonly busy: boolean;
  /** What the API said about the last attempt, or `null`. */
  readonly refusal: ApiRefusal | null;
}

type Mode = 'sign-in' | 'register';

export const SignInScreen = ({ reason, onSignIn, onRegister, busy, refusal }: SignInScreenProps): ReactElement => {
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [checked, setChecked] = useState<readonly FieldMessage[]>([]);

  const registering = mode === 'register';
  const notice = NOTICES[reason];

  const submit = (event: SyntheticEvent<HTMLFormElement>): void => {
    event.preventDefault();

    const messages = registering ? checkRegistration({ email, password, displayName }) : checkSignIn({ email, password });
    setChecked(messages);
    if (messages.length > 0) return;

    if (registering) onRegister({ email, password, displayName });
    else onSignIn({ email, password });
  };

  const change = (next: Mode): void => {
    setMode(next);
    // The form's own complaints belong to the form that was on screen; carrying
    // them across would show a registration's password rule on a sign-in box,
    // which is the one thing a sign-in box must not state.
    setChecked([]);
  };

  const fieldError = (field: string): ReactElement | null => {
    const message = messageFor(checked, field);

    return message === null ? null : (
      <p className="field__error" data-testid={`error-${field}`}>
        {message}
      </p>
    );
  };

  return (
    <section className="panel" data-testid="sign-in-screen">
      <h2 className="panel__title">{registering ? 'Create an account' : 'Sign in'}</h2>

      <p className="panel__lede" data-testid="signed-out-lede">
        You are not signed in. A character is tied to an account, and the world will not seat one without it.
      </p>

      {notice !== null && (
        <p className="panel__notice" role="status" data-testid="signed-out-notice">
          {notice}
        </p>
      )}

      <form className="form" onSubmit={submit} noValidate>
        <label className="field">
          <span className="field__label">Email</span>
          <input
            className="field__input"
            data-testid="email"
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(event): void => {
              setEmail(event.target.value);
            }}
          />
          {fieldError('email')}
        </label>

        {registering && (
          <label className="field">
            <span className="field__label">Display name</span>
            <input
              className="field__input"
              data-testid="displayName"
              type="text"
              name="displayName"
              autoComplete="nickname"
              value={displayName}
              onChange={(event): void => {
                setDisplayName(event.target.value);
              }}
            />
            {fieldError('displayName')}
          </label>
        )}

        <label className="field">
          <span className="field__label">Password</span>
          <input
            className="field__input"
            data-testid="password"
            type="password"
            name="password"
            // `new-password` on the registration form is what stops a browser
            // filling a *chosen* password in from a saved one.
            autoComplete={registering ? 'new-password' : 'current-password'}
            value={password}
            onChange={(event): void => {
              setPassword(event.target.value);
            }}
          />
          {fieldError('password')}
        </label>

        {refusal !== null && (
          <p className="form__error" role="alert" data-testid="auth-error">
            {describeRefusal(refusal)}
          </p>
        )}

        <button className="button" data-testid="submit" type="submit" disabled={busy}>
          {registering ? 'Create account' : 'Sign in'}
        </button>
      </form>

      <p className="panel__aside">
        {registering ? 'Already have an account? ' : 'No account yet? '}
        <button
          className="button button--link"
          data-testid="switch-mode"
          type="button"
          onClick={(): void => {
            change(registering ? 'sign-in' : 'register');
          }}
        >
          {registering ? 'Sign in' : 'Create one'}
        </button>
      </p>

      {/*
       * Stated rather than linked. There is no reset endpoint and no mail path
       * to build one on (`TODO.md` #390), and a player who is told the truth can
       * at least make a second account instead of waiting for an email that will
       * never arrive.
       */}
      <p className="panel__aside panel__aside--quiet" data-testid="no-reset-notice">
        There is no password recovery yet. A forgotten password cannot be restored.
      </p>
    </section>
  );
};
